<template>
  <div class="telegram-integration">
    <empty-card :title="$t('telegram.title')">
      <template #title-left>
        <div class="telegram-section-header d-flex align-items-center justify-content-between">
          <span class="telegram-section-title">
            {{ $t('telegram.title') }}
          </span>
          <b-link
            :to="{ name: 'owner:home' }"
            class="back-link d-inline-flex align-items-center mr-3"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="20"
              class="mr-50"
            />
            {{ $t('forms.back') }}
          </b-link>

        </div>
      </template>

      <b-link
        :to="{ name: 'owner:home' }"
        class="back-link d-inline-flex align-items-center mb-3 d-sm-none"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="18"
          class="mr-50"
        />
        {{ $t('forms.back') }}
      </b-link>

      <b-row>
        <!-- Hero + benefits card -->
        <b-col
          cols="12"
          class="mb-2"
        >
          <div class="telegram-hero-card mt-4">
            <div class="telegram-hero-bg" />
            <div class="telegram-hero-content">
              <div class="telegram-hero-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="32"
                  height="32"
                >
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                </svg>
              </div>
              <h2 class="telegram-hero-title">
                {{ $t('telegram.info_title') }}
              </h2>
              <p class="telegram-hero-desc">
                {{ $t('telegram.info_description') }}
              </p>
              <div class="telegram-benefits">
                <p class="telegram-benefits-heading">
                  {{ $t('telegram.benefits_title') }}
                </p>
                <ul class="telegram-benefits-list">
                  <li class="telegram-benefit-item">
                    <span class="telegram-benefit-check"><feather-icon
                      icon="CheckIcon"
                      size="18"
                    /></span>
                    {{ $t('telegram.benefit_pages') }}
                  </li>
                  <li class="telegram-benefit-item">
                    <span class="telegram-benefit-check"><feather-icon
                      icon="CheckIcon"
                      size="18"
                    /></span>
                    {{ $t('telegram.benefit_auto_replies') }}
                  </li>
                  <li class="telegram-benefit-item">
                    <span class="telegram-benefit-check"><feather-icon
                      icon="CheckIcon"
                      size="18"
                    /></span>
                    {{ $t('telegram.benefit_expiry_reminder') }}
                  </li>
                  <li class="telegram-benefit-item">
                    <span class="telegram-benefit-check"><feather-icon
                      icon="CheckIcon"
                      size="18"
                    /></span>
                    {{ $t('telegram.benefit_offers') }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </b-col>

        <!-- Status card -->
        <b-col
          :cols="12"
          :lg="linkCode ? 5 : 12"
          class="mb-3"
        >
          <b-card
            no-body
            class="telegram-status-card h-100"
          >
            <b-card-body>
              <h6 class="telegram-card-label">
                {{ $t('telegram.status') }}
              </h6>
              <div class="d-flex align-items-center flex-wrap">
                <template v-if="status.linked">
                  <b-avatar
                    variant="success"
                    size="44"
                    class="mr-3 telegram-avatar"
                  >
                    <feather-icon
                      icon="CheckIcon"
                      size="22"
                    />
                  </b-avatar>
                  <div>
                    <b-badge
                      variant="success"
                      class="telegram-badge telegram-badge-success"
                    >
                      {{ $t('telegram.status_linked') }}
                    </b-badge>
                    <p class="text-muted small mb-0 mt-1 telegram-chat-id">
                      {{ $t('telegram.chat_id', { id: status.telegram_chat_id }) }}
                    </p>
                  </div>
                </template>
                <template v-else>
                  <b-avatar
                    variant="light-secondary"
                    size="44"
                    class="mr-3 telegram-avatar"
                  >
                    <feather-icon
                      icon="CircleIcon"
                      size="22"
                    />
                  </b-avatar>
                  <b-badge
                    variant="secondary"
                    class="telegram-badge"
                  >
                    {{ $t('telegram.status_not_linked') }}
                  </b-badge>
                </template>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Link code card -->
        <b-col
          v-if="linkCode"
          cols="12"
          lg="7"
          class="mb-3"
        >
          <b-card
            no-body
            class="telegram-code-card h-100"
          >
            <b-card-body>
              <h6 class="telegram-card-label telegram-card-label-primary">
                {{ $t('telegram.link_code') }}
              </h6>
              <div class="telegram-code-block">
                {{ linkCode }}
              </div>
              <p class="text-muted small mb-3 telegram-code-helper">
                {{ $t('telegram.link_code_helper') }}
              </p>
              <div class="d-flex flex-wrap align-items-center">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2 mb-1 telegram-btn telegram-btn-primary"
                  @click="copyCode"
                >
                  <feather-icon
                    icon="CopyIcon"
                    size="16"
                    class="mr-50"
                  />
                  {{ $t('telegram.copy_code') }}
                </b-button>
                <b-button
                  :href="'https://t.me/' + telegramBotUsername + (linkCode ? '?start=' + encodeURIComponent(linkCode) : '')"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="success"
                  size="sm"
                  class="mb-1 telegram-btn telegram-btn-telegram"
                >
                  <feather-icon
                    icon="SendIcon"
                    size="16"
                    class="mr-50"
                  />
                  {{ $t('telegram.open_telegram_bot') }}
                </b-button>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Actions -->
        <b-col
          cols="12"
          class="mb-2"
        >
          <b-card
            no-body
            class="telegram-actions-card"
          >
            <b-card-body class="d-flex flex-wrap align-items-center">
              <b-button
                v-if="!status.linked && !linkCode"
                variant="primary"
                :disabled="loading"
                class="mr-2 mb-1 telegram-btn telegram-btn-cta"
                @click="generateCode"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="KeyIcon"
                  class="mr-50"
                />
                {{ $t('telegram.generate_link_code') }}
              </b-button>
              <b-button
                v-if="status.linked"
                variant="outline-danger"
                :disabled="loading"
                class="mb-1 telegram-btn"
                @click="unlinkAccount"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="XCircleIcon"
                  class="mr-50"
                />
                {{ $t('telegram.unlink_telegram') }}
              </b-button>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- User posting bot -->
        <b-col
          cols="12"
          class="mb-2"
        >
          <b-card no-body>
            <b-card-body>
              <h6 class="telegram-card-label">
                {{ $t('telegram.posting_bot_title') }}
              </h6>
              <p class="text-muted small">
                {{ $t('telegram.posting_bot_hint') }}
              </p>
              <p
                v-if="bot.configured"
                class="mb-1"
              >
                <b-badge variant="success">
                  @{{ bot.bot_username || 'bot' }}
                </b-badge>
                <span
                  v-if="bot.bot_token_hint"
                  class="text-muted small ml-1"
                >{{ bot.bot_token_hint }}</span>
              </p>
              <ok-text-input
                v-model="botToken"
                :label="$t('telegram.bot_token')"
                :placeholder="bot.configured ? '••••••••' : '123456:ABC-DEF...'"
              />
              <div class="d-flex flex-wrap mt-1">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-1 mb-1"
                  :disabled="botLoading || !botToken"
                  @click="saveBot"
                >
                  {{ $t('telegram.save_bot') }}
                </b-button>
                <b-button
                  v-if="bot.configured"
                  variant="outline-primary"
                  size="sm"
                  class="mr-1 mb-1"
                  :disabled="botLoading"
                  @click="detectChats"
                >
                  {{ $t('telegram.detect_chats') }}
                </b-button>
                <b-button
                  v-if="bot.configured"
                  variant="outline-danger"
                  size="sm"
                  class="mb-1"
                  :disabled="botLoading"
                  @click="removeBot"
                >
                  {{ $t('telegram.remove_bot') }}
                </b-button>
              </div>
              <div
                v-if="bot.configured"
                class="mt-2"
              >
                <ok-text-input
                  v-model="verifyChat"
                  :label="$t('telegram.add_chat_label')"
                  placeholder="@channel or -100..."
                />
                <b-button
                  size="sm"
                  variant="success"
                  class="mt-1"
                  :disabled="botLoading"
                  @click="addChat"
                >
                  {{ $t('telegram.add_chat') }}
                </b-button>
                <div
                  v-if="destinations.length"
                  class="mt-2"
                >
                  <div
                    v-for="d in destinations"
                    :key="d.id"
                    class="small mb-50"
                  >
                    {{ d.title || d.username || d.chat_id }}
                    <span class="text-muted">({{ d.type }})</span>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </empty-card>
  </div>
</template>

<script>
import EmptyCard from '@/@bya3/components/cards/EmptyCard'
import UserTelegramApi from '@/api/user/telegram'
import OkTextInput from '@/@bya3/components/inputs/OkTextInput'
import {
  BBadge, BSpinner, BButton, BCard, BCardBody, BLink, BAvatar, BRow, BCol,
} from 'bootstrap-vue'

export default {
  name: 'TelegramIntegration',
  components: {
    EmptyCard,
    OkTextInput,
    BBadge,
    BSpinner,
    BButton,
    BCard,
    BCardBody,
    BLink,
    BAvatar,
    BRow,
    BCol,
  },
  data() {
    return {
      status: { linked: false, telegram_chat_id: null },
      linkCode: '',
      botUsername: '',
      loading: false,
      botLoading: false,
      botToken: '',
      verifyChat: '',
      destinations: [],
      bot: {
        configured: false,
        bot_username: null,
        bot_token_hint: null,
      },
    }
  },
  computed: {
    telegramBotUsername() {
      return this.botUsername || 'Bya3comReplierBot'
    },
  },
  mounted() {
    this.fetchStatus()
    this.fetchBot()
    this.loadDestinations()
  },
  methods: {
    async fetchStatus() {
      try {
        this.status = await UserTelegramApi.getStatus()
      } catch (e) {
        this.$notifyError(this.$t('telegram.error_loading'))
      }
    },
    async generateCode() {
      this.loading = true
      try {
        const res = await UserTelegramApi.generateLinkCode()
        const payload = res && res.data ? res.data : res
        const code = payload?.link_code || ''
        this.linkCode = code
        this.botUsername = payload?.bot_username || ''
        this.$notifySuccess(this.$t('telegram.link_code_generated'))
        if (code) {
          try {
            await this.copyToClipboard(code)
            this.$notifySuccess(this.$t('telegram.code_copied'))
          } catch (e) {
            console.log(e)
          }
        }
      } catch (e) {
        const msg = e?.response?.data?.message || this.$t('telegram.error_generating')
        this.$notifyError(msg)
      } finally {
        this.loading = false
      }
    },
    copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text)
      }
      const el = document.createElement('textarea')
      el.value = text
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.select()
      try {
        document.execCommand('copy')
        return Promise.resolve()
      } finally {
        document.body.removeChild(el)
      }
    },
    copyCode() {
      if (!this.linkCode) return
      this.copyToClipboard(this.linkCode).then(() => {
        this.$notifySuccess(this.$t('telegram.code_copied'))
      }).catch(() => {
        this.$notifyError(this.$t('telegram.copy_failed'))
      })
    },
    async unlinkAccount() {
      const ok = await this.$okConfirm(this.$t('telegram.unlink_confirm'))
      if (!ok) return
      this.loading = true
      try {
        await UserTelegramApi.unlink()
        this.status = { linked: false, telegram_chat_id: null }
        this.linkCode = ''
        this.$notifySuccess(this.$t('telegram.telegram_unlinked'))
      } catch (e) {
        this.$notifyError(this.$t('telegram.error_unlink'))
      } finally {
        this.loading = false
      }
    },
    async fetchBot() {
      try {
        const res = await UserTelegramApi.botStatus()
        this.bot = {
          configured: !!res.configured,
          bot_username: res.bot_username,
          bot_token_hint: res.bot_token_hint,
        }
      } catch (e) {
        // ignore
      }
    },
    async loadDestinations() {
      try {
        const res = await UserTelegramApi.destinations()
        this.destinations = res.destinations || []
        if (res.configured !== undefined) {
          this.bot.configured = !!res.configured
          this.bot.bot_username = res.bot_username
        }
      } catch (e) {
        // ignore
      }
    },
    async saveBot() {
      this.botLoading = true
      try {
        const res = await UserTelegramApi.saveBot(this.botToken)
        if (res.error === 1) {
          this.$notifyError(res.message)
        } else {
          this.$notifySuccess(res.message)
          this.botToken = ''
          await this.fetchBot()
        }
      } catch (e) {
        this.$notifyError(e)
      } finally {
        this.botLoading = false
      }
    },
    async removeBot() {
      this.botLoading = true
      try {
        const res = await UserTelegramApi.removeBot()
        this.$notifySuccess(res.message)
        await this.fetchBot()
        this.destinations = []
      } catch (e) {
        this.$notifyError(e)
      } finally {
        this.botLoading = false
      }
    },
    async detectChats() {
      this.botLoading = true
      try {
        const res = await UserTelegramApi.refreshDestinations()
        if (res.error === 1) {
          this.$notifyError(res.message)
        } else {
          this.$notifySuccess(res.message)
          this.destinations = res.destinations || []
        }
      } catch (e) {
        this.$notifyError(e)
      } finally {
        this.botLoading = false
      }
    },
    async addChat() {
      this.botLoading = true
      try {
        const res = await UserTelegramApi.verifyDestination(this.verifyChat)
        if (res.error === 1) {
          this.$notifyError(res.message)
        } else {
          this.$notifySuccess(res.message)
          this.verifyChat = ''
          await this.loadDestinations()
        }
      } catch (e) {
        this.$notifyError(e)
      } finally {
        this.botLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.telegram-integration {
  --tg-primary: #0088cc;
  --tg-primary-dark: #006699;
  --tg-primary-light: #00a2e8;
  --tg-bg: #e8f4fc;
  --tg-card-radius: 14px;
  --tg-shadow: 0 4px 20px rgba(0, 136, 204, 0.08);
  --tg-shadow-hover: 0 8px 28px rgba(0, 136, 204, 0.12);

  .telegram-section-header {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .telegram-section-title {
    font-size: 25px;
    line-height: 1.2;
    color: var(--purple);
    font-weight: 700;
  }

  .back-link {
    color: var(--body-color);
    text-decoration: none;
    transition: color 0.15s ease;
    font-weight: 500;

    &:hover {
      color: var(--tg-primary);
    }
  }

  .telegram-hero-card {
    position: relative;
    border-radius: var(--tg-card-radius);
    overflow: hidden;
    box-shadow: var(--tg-shadow);
    background: linear-gradient(145deg, #fff 0%, var(--tg-bg) 100%);
    border: 1px solid rgba(0, 136, 204, 0.12);
  }

  .telegram-hero-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(135deg, var(--tg-primary) 0%, var(--tg-primary-light) 100%);
    opacity: 0.06;
  }

  .telegram-hero-content {
    position: relative;
    padding: 1.75rem 1.5rem;
  }

  .telegram-hero-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: linear-gradient(135deg, var(--tg-primary) 0%, var(--tg-primary-light) 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    box-shadow: 0 6px 16px rgba(0, 136, 204, 0.35);
  }

  .telegram-hero-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--body-color);
    margin-bottom: 0.5rem;
  }

  .telegram-hero-desc {
    color: var(--body-color);
    opacity: 0.85;
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 1.25rem;
  }

  .telegram-benefits {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 10px;
    padding: 1rem 1.25rem;
    border: 1px solid rgba(0, 136, 204, 0.1);
  }

  .telegram-benefits-heading {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--body-color);
    margin-bottom: 0.75rem;
    opacity: 0.9;
  }

  .telegram-benefits-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .telegram-benefit-item {
    display: flex;
    align-items: center;
    padding: 0.4rem 0;
    font-size: 0.9rem;
    color: var(--body-color);
    line-height: 1.4;
  }

  .telegram-benefit-check {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--tg-primary) 0%, var(--tg-primary-light) 100%);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-inline-end: 0.65rem;
  }

  .telegram-status-card,
  .telegram-code-card,
  .telegram-actions-card {
    border-radius: var(--tg-card-radius);
    box-shadow: var(--tg-shadow);
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: var(--tg-shadow-hover);
    }
  }

  .telegram-card-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--body-color);
    opacity: 0.7;
    margin-bottom: 0.75rem;
  }

  .telegram-card-label-primary {
    color: var(--tg-primary);
    opacity: 1;
  }

  .telegram-avatar {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .telegram-badge {
    font-weight: 500;
    padding: 0.35em 0.65em;
  }

  .telegram-badge-success {
    background: linear-gradient(135deg, #0c9 0%, #0a8 100%) !important;
    border: none;
  }

  .telegram-chat-id {
    font-family: ui-monospace, monospace;
    font-size: 0.8rem;
  }

  .telegram-code-card {
    border-width: 2px;
    border-color: rgba(0, 136, 204, 0.2) !important;
    background: linear-gradient(180deg, #fff 0%, rgba(0, 136, 204, 0.03) 100%);
  }

  .telegram-code-block {
    background: rgba(0, 136, 204, 0.08);
    border: 1px solid rgba(0, 136, 204, 0.2);
    border-radius: 10px;
    padding: 0.75rem 1rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    font-size: 1.05rem;
    font-family: ui-monospace, monospace;
    margin-bottom: 0.75rem;
  }

  .telegram-code-helper {
    font-size: 0.85rem;
  }

  .telegram-btn {
    font-weight: 500;
    border-radius: 8px;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  .telegram-btn-primary {
    background: linear-gradient(135deg, var(--tg-primary) 0%, var(--tg-primary-light) 100%);
    border: none;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--tg-primary-dark) 0%, var(--tg-primary) 100%);
    }
  }

  .telegram-btn-telegram {
    background: linear-gradient(135deg, var(--tg-primary) 0%, var(--tg-primary-light) 100%);
    border: none;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--tg-primary-dark) 0%, var(--tg-primary) 100%);
    }
  }

  .telegram-btn-cta {
    padding: 0.6rem 1.25rem;
    font-size: 1rem;
    background: linear-gradient(135deg, var(--tg-primary) 0%, var(--tg-primary-light) 100%);
    border: none;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--tg-primary-dark) 0%, var(--tg-primary) 100%);
    }
  }
}
</style>
